import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { expandTdDivsAnimation, fadeInAnimation, onlyEnter } from '@zero/shared/ui-animations';
import { DisplayLabelPipe } from '@zero/shared/ui-pipes';
import { SortHelper } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { isEqual } from '@zero/shared/util-helper';
import { PushMessageType, Ticket, UserDataModel } from '@zero/shared/util-models';
import { DataService, UserDataService } from '@zero/shared/util-web-app-services';
import { TicketFacade } from '@zero/tickets/domain';
import { assign, differenceBy, intersectionBy, isEqualWith } from 'lodash-es';
import { Observable, take } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { TicketCreationState } from '../../models/ticket-creation-state';
import { CreateTicketComponent } from '../create-ticket/create-ticket.component';
import { ShowTicketThreadComponent } from '../show-ticket-thread/show-ticket-thread.component';
import { ZendeskFaqSearchComponent } from '../zendesk-faq-search/zendesk-faq-search.component';

export type CreateTicketDialogType = 'createTicket' | 'showFaq';
export type AllowedStatus = 'ALL' | 'OPEN' | 'CLOSED';

@Component({
  selector: 'tickets-ticket-list',
  templateUrl: './ticket-list.component.html',
  styleUrls: ['./ticket-list.component.scss'],
  animations: [fadeInAnimation, expandTdDivsAnimation, onlyEnter],
  imports: [
    ZendeskFaqSearchComponent,
    CreateTicketComponent,
    NgClass,
    ShowTicketThreadComponent,
    DatePipe,
    DisplayLabelPipe,
    TranslocoPipe,
    AsyncPipe,
    ButtonComponent,
  ],
})
export class TicketListComponent implements OnInit {
  public sorter: SortHelper = new SortHelper('created', false, 'tickets');
  public tickets: Ticket[] = [];
  public ticketsHasNext = false;
  public ticketsToShow = {};
  public ticketsLastLoaded: number;
  public currentUser$: Observable<UserDataModel>;

  public dialogMode: CreateTicketDialogType;

  public allowedStatus: AllowedStatus[] = ['ALL', 'OPEN', 'CLOSED'];
  public currentStatus: AllowedStatus = 'ALL';
  private readonly PAGE_SIZE = 10;
  private currentPage = 0;

  readonly #userService = inject(UserDataService);
  readonly #destroyRef = inject(DestroyRef);
  public readonly rootData = inject(DataService);
  public readonly ticketFacade = inject(TicketFacade);

  public ngOnInit(): void {
    this.currentUser$ = this.#userService.loadContactData$(false).pipe(filter((result) => !!result));
    this.subscribeToPushMessages();
    this.subscribeToTickets();

    this.loadTicketsByCurrentPage();
    this.ticketFacade.loadUnreadTickets(true);
  }

  private subscribeToPushMessages(): void {
    this.rootData.msgSubject
      .pipe(
        filter((msg) => msg.type === PushMessageType.ticket),
        debounceTime(500),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        //reload the current size of tickets to avoid disappearing list after push message
        this.ticketFacade.loadTickets(true, 0, this.currentPage * this.PAGE_SIZE + this.PAGE_SIZE, this.currentStatus);
        this.ticketFacade.loadUnreadTickets(true);
      });
  }

  private subscribeToTickets(): void {
    this.ticketFacade.tickets$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((tickets) => {
      if (this.currentPage > 0) {
        this.appendTickets(this.tickets, tickets);
      } else {
        this.tickets = tickets;
      }
      this.sorter.sortValues(this.tickets);
      this.ticketsLastLoaded = Date.now();
    });
  }

  private loadTicketsByCurrentPage(): void {
    this.ticketFacade.loadTickets(true, this.currentPage, this.PAGE_SIZE, this.currentStatus);
  }

  public sortBy(sortAttr: string): void {
    this.sorter.sortBy(this.tickets, sortAttr);
  }

  public ticketDetails(t: Ticket): void {
    this.ticketsToShow[t.id] = !this.ticketsToShow[t.id];
  }

  public getAnlagen(t: Ticket): any[] {
    let allEntries: any[] = [];

    if (t.deskTicket?.descAttachments) {
      allEntries = allEntries.concat(t.deskTicket.descAttachments);
    }

    if (t.deskComments) {
      for (const c of t.deskComments) {
        if (c.attachments) {
          allEntries = allEntries.concat(c.attachments);
        }
      }
    }
    if (t.deskThreads) {
      for (const th of t.deskThreads) {
        if (th.attachments) {
          allEntries = allEntries.concat(th.attachments);
        }
      }
    }
    return allEntries;
  }

  public createTicket(): void {
    this.dialogMode = 'createTicket';
  }

  public ticketCreationStateUpdate(event: TicketCreationState): void {
    if (event.status === 'finished') {
      this.onTicketChanged();
    }
    this.dialogMode = undefined;
  }

  public onTicketChanged(): void {
    // we wait a short time before we reload since after a change, Zoho has to notify our systems
    // and then the database has to be updated until we can see the change
    setTimeout(() => {
      //reload the current size of tickets to avoid disappearing list after push message
      this.ticketFacade.loadTickets(true, 0, this.currentPage * this.PAGE_SIZE + this.PAGE_SIZE, this.currentStatus);
      this.ticketFacade.loadUnreadTickets(true);
    }, 1000);
  }

  public changeCategory(cat: AllowedStatus): void {
    this.currentStatus = cat;
    this.ticketsToShow = {};
    this.currentPage = 0;
    this.loadTicketsByCurrentPage();
  }

  public showMore(): void {
    this.ticketFacade.ticketsHasNext$
      .pipe(
        filter((hasNext) => !!hasNext),
        take(1),
      )
      .subscribe(() => {
        this.currentPage++;
        this.loadTicketsByCurrentPage();
      });
  }

  private comparatorForMerge(t1: Ticket, t2: Ticket): boolean {
    return isEqual(JSON.stringify(t1), JSON.stringify(t2));
  }

  private appendTickets(currentTickets: Ticket[], ticketToMerge: Ticket[]): void {
    const mergePropertyName = 'id';
    const toAdd = differenceBy(ticketToMerge, currentTickets, mergePropertyName);
    currentTickets.push(...toAdd);

    const docIdsToUpdate = intersectionBy(currentTickets, ticketToMerge, mergePropertyName).map((doc) => doc[mergePropertyName]);
    currentTickets.forEach((currDoc) => {
      if (docIdsToUpdate.indexOf(currDoc[mergePropertyName]) > -1) {
        const docToMerge = ticketToMerge.find((o) => o[mergePropertyName] === currDoc[mergePropertyName]);
        if (!isEqualWith(currDoc, docToMerge, this.comparatorForMerge)) {
          assign(currDoc, docToMerge);
        }
      }
    });
  }
}
